'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { UserCircle, Package, Award, LogOut, Wallet } from 'lucide-react';

import CampaignAnnouncementStrip from '@/components/CampaignAnnouncementStrip';
import { shouldUseWalletAnnouncementAction } from '@/components/campaignAnnouncementUtils';
import { useWalletGiftAnnouncement } from '@/presentation/hooks/useWalletGiftAnnouncement';
import PartnerHeader from '@/components/PartnerHeader';
import {
  buildOrderedHeroBanners,
  getCampaignAnnouncement,
  getRewardOfferAnnouncement,
  inferAnnouncementSurface,
} from '@/components/marketplaceHomeContent';
import ReputationBadge from '@/components/ReputationBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchBar } from '@/components/ui/search';
import { usePartner } from '@/presentation/hooks/usePartner';
import {
  useAuthStore,
  useModalStore,
  useProductStore,
  useRepScoreStore,
  useShoppingWalletStore,
} from '@/presentation/hooks/useStores';
import { type ScrollDirection, useScrollDirection } from '@/presentation/hooks/useScrollDirection';

import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../ui/popover';
import RepScoreDrawer from '../drawers/RepScoreDrawer';
import Pay4MeDrawer from '../drawers/Pay4MeDrawer';
import ShoppingWalletDrawer from '../drawers/ShoppingWalletDrawer';
import HelpCenterSheet from '../modals/HelpCenterSheet';
import MobileSideDrawer from './MobileSideDrawer';
import RequestCallbackModal from './RequestCallbackModal';
import HeaderSecondaryNav, { type HeaderSecondaryNavVariant } from './HeaderSecondaryNav';
import UserNotificationsPopover from './UserNotificationsPopover';
import { getLargePageScrollContainerScrollY } from './headerScrollSource';
import {
  resolveMobileSecondaryNavState,
  type MobileSecondaryNavTransitionState,
} from './mobileSecondaryNavController';

/**
 * =============================================================================
 * MOBILE NAVIGATION CONFIGURATION
 * =============================================================================
 *
 * This configuration determines which navigation variant to show for each route.
 * The Header uses this to render the appropriate HeaderSecondaryNav component.
 *
 * Navigation Variants:
 * - 'back-help': Back button + Help button (transactional pages)
 * - 'back-search': Back button + Search bar (browse pages)
 * - 'search-only': Just search bar (general pages)
 * - null: No secondary nav (profile, etc.)
 *
 * Route matching is done by prefix (startsWith), so '/order' matches '/order/123/pay'
 * =============================================================================
 */
type NavConfig = {
  pathPrefix: string;
  variant: HeaderSecondaryNavVariant | null;
};

const MOBILE_NAV_CONFIG: NavConfig[] = [
  // Transactional pages - Back + Help
  { pathPrefix: '/checkout', variant: 'back-help' },

  // Order detail pages get back+help (sub-pages within flow)
  // Note: More specific paths must come BEFORE less specific ones
  { pathPrefix: '/order/', variant: 'back-help' },

  // Browse pages - Back + Search (with scroll show/hide behavior)
  { pathPrefix: '/products', variant: 'back-search' },
  { pathPrefix: '/category', variant: 'back-search' },
  { pathPrefix: '/collections/', variant: 'back-search' },
  { pathPrefix: '/brands/', variant: 'back-search' },

  // Root sections - no secondary nav (these are destinations, not steps in a flow)
  { pathPrefix: '/order', variant: null }, // Orders list is a root section
  { pathPrefix: '/profile', variant: null },
  { pathPrefix: '/onboarding', variant: null },
  { pathPrefix: '/sign-in', variant: null },
  { pathPrefix: '/sign-up', variant: null },
];

const WALLET_PRELOAD_RETRY_MS = 15_000;
const MOBILE_NAV_DIRECTION_THRESHOLD = 12;
const MOBILE_NAV_TOP_THRESHOLD = 24;
const MOBILE_NAV_FALLBACK_HEIGHT: Record<Exclude<HeaderSecondaryNavVariant, never>, number> = {
  'back-help': 72,
  'back-search': 76,
  'search-only': 76,
};

// Helper to get nav variant for a path
const getNavVariant = (pathname: string | null): HeaderSecondaryNavVariant | null => {
  if (!pathname) return 'search-only'; // Default to search for SSR

  // Check configured paths
  for (const config of MOBILE_NAV_CONFIG) {
    if (pathname.startsWith(config.pathPrefix)) {
      return config.variant;
    }
  }

  // Default: show search-only for unconfigured paths (homepage, etc.)
  return 'search-only';
};

/**
 * =============================================================================
 * SMART STICKY HEADER WITH COLLAPSIBLE SEARCH BAR
 * =============================================================================
 *
 * This header implements a "smart" sticky behavior where:
 *
 * 1. MAIN HEADER (logo + login) - Always sticky at the top
 * 2. SEARCH BAR - Hides on scroll down, reveals on scroll up
 *
 * WHY THIS PATTERN?
 * -----------------
 * - Scroll DOWN = User is browsing/exploring content → hide search to maximize space
 * - Scroll UP = User wants to go back or search for something → reveal search
 * - At TOP = Show everything since there's no content to prioritize
 *
 * HOW TO REPLICATE ON OTHER SCREENS
 * ----------------------------------
 * 1. Import: import { useScrollDirection } from '@/presentation/hooks/useScrollDirection';
 * 2. Use hook: const { scrollDirection, isAtTop } = useScrollDirection();
 * 3. Compute visibility: const showElement = isAtTop || scrollDirection === 'up';
 * 4. Apply CSS:
 *    - Container: overflow-hidden transition-all duration-300 ease-in-out
 *    - Show: max-h-20 opacity-100
 *    - Hide: max-h-0 opacity-0
 *
 * =============================================================================
 */
const Header = observer(() => {
  const router = useRouter();
  const pathname = usePathname();
  const authStore = useAuthStore();
  const modalStore = useModalStore();
  const productStore = useProductStore();
  const repScoreStore = useRepScoreStore();
  const shoppingWalletStore = useShoppingWalletStore();
  const {
    isPartnerDomain,
    partnerKey,
    isLoading: isPartnerLoading,
  } = usePartner();
  const menuItemList = productStore.categoryNavigation;

  // State for mobile side drawer
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isCallbackModalOpen, setCallbackModalOpen] = useState(false);
  const [isMobileNavVisible, setIsMobileNavVisible] = useState(true);
  const [mobileSecondaryTopOffset, setMobileSecondaryTopOffset] = useState(0);
  const [searchOnlyContainerScrollY, setSearchOnlyContainerScrollY] = useState(0);
  const [effectiveScrollDirection, setEffectiveScrollDirection] = useState<ScrollDirection>(null);
  const [walletRetryTick, setWalletRetryTick] = useState(0);
  const primaryStickyShellRef = useRef<HTMLDivElement | null>(null);
  const mobileSecondaryLayerRef = useRef<HTMLDivElement | null>(null);
  const navVisibilityAnchorRef = useRef(0);
  const navTransitionStateRef = useRef<MobileSecondaryNavTransitionState>('settled');
  const lastEffectiveScrollYRef = useRef(0);
  const lastVisibleMobileSecondaryHeightRef = useRef(0);
  const walletRetryTimeoutRef = useRef<number | null>(null);
  const lastWalletUserIdRef = useRef<string | null>(null);

  const clearWalletRetryTimeout = useCallback(() => {
    if (walletRetryTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(walletRetryTimeoutRef.current);
    walletRetryTimeoutRef.current = null;
  }, []);

  const { scrollY } = useScrollDirection({
    threshold: MOBILE_NAV_DIRECTION_THRESHOLD,
    topThreshold: MOBILE_NAV_TOP_THRESHOLD,
  });
  const mobileNavVariant = getNavVariant(pathname);
  const effectiveScrollY =
    mobileNavVariant === 'search-only'
      ? Math.max(scrollY, searchOnlyContainerScrollY)
      : scrollY;
  const isEffectivelyAtTop = effectiveScrollY <= MOBILE_NAV_TOP_THRESHOLD;

  const syncPrimaryStickyHeight = useCallback(() => {
    if (!primaryStickyShellRef.current) {
      return;
    }

    const nextHeight = Math.ceil(primaryStickyShellRef.current.getBoundingClientRect().height);
    setMobileSecondaryTopOffset((previousHeight) => (
      previousHeight === nextHeight ? previousHeight : nextHeight
    ));
  }, []);

  useLayoutEffect(() => {
    syncPrimaryStickyHeight();

    if (typeof window === 'undefined') {
      return undefined;
    }

    const stickyShell = primaryStickyShellRef.current;
    if (!stickyShell) {
      return undefined;
    }

    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        syncPrimaryStickyHeight();
      });
      resizeObserver.observe(stickyShell);
    }

    window.addEventListener('resize', syncPrimaryStickyHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncPrimaryStickyHeight);
    };
  }, [syncPrimaryStickyHeight]);

  useLayoutEffect(() => {
    if (
      typeof window === 'undefined' ||
      !mobileNavVariant ||
      !isMobileNavVisible ||
      !mobileSecondaryLayerRef.current
    ) {
      return undefined;
    }

    const nextHeight = Math.ceil(
      mobileSecondaryLayerRef.current.getBoundingClientRect().height
    );
    if (nextHeight > 0) {
      lastVisibleMobileSecondaryHeightRef.current = nextHeight;
    }

    return undefined;
  }, [isMobileNavVisible, mobileNavVariant, pathname]);

  useEffect(() => {
    const currentScrollY = typeof window === 'undefined' ? 0 : window.scrollY;

    if (!mobileNavVariant) {
      setIsMobileNavVisible(false);
      navVisibilityAnchorRef.current = currentScrollY;
      navTransitionStateRef.current = 'settled';
      lastEffectiveScrollYRef.current = currentScrollY;
      setEffectiveScrollDirection(null);
      return;
    }

    setIsMobileNavVisible(true);
    navVisibilityAnchorRef.current = currentScrollY;
    navTransitionStateRef.current = 'settled';
    lastEffectiveScrollYRef.current = currentScrollY;
    setEffectiveScrollDirection(null);
  }, [mobileNavVariant, pathname]);

  useEffect(() => {
    if (mobileNavVariant !== 'search-only') {
      setSearchOnlyContainerScrollY(0);
      return;
    }

    const handleDocumentScroll = (event: Event) => {
      const nextScrollY = getLargePageScrollContainerScrollY(event.target);
      if (nextScrollY === null) {
        return;
      }

      setSearchOnlyContainerScrollY((current) => (
        current === nextScrollY ? current : nextScrollY
      ));
    };

    document.addEventListener('scroll', handleDocumentScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener('scroll', handleDocumentScroll, true);
    };
  }, [mobileNavVariant]);

  useEffect(() => {
    const scrollDiff = effectiveScrollY - lastEffectiveScrollYRef.current;

    if (Math.abs(scrollDiff) >= MOBILE_NAV_DIRECTION_THRESHOLD) {
      setEffectiveScrollDirection(scrollDiff > 0 ? 'down' : 'up');
      lastEffectiveScrollYRef.current = effectiveScrollY;
    }
  }, [effectiveScrollY]);

  /**
   * MOBILE SEARCH BAR VISIBILITY LOGIC
   * -----------------------------------
   * Preserve the PDP interaction that was already approved:
   * - easy reveal on clear upward reversal
   * - deliberate hide on sustained downward browsing
   * Homepage shares this same state machine; it only gets a broader
   * scroll source so the same interaction works there too.
   */
  useEffect(() => {
    const liveMobileNavHeight = Math.ceil(
      mobileSecondaryLayerRef.current?.getBoundingClientRect().height ?? 0
    );
    const mobileNavHeight = Math.max(
      liveMobileNavHeight,
      lastVisibleMobileSecondaryHeightRef.current,
      mobileNavVariant ? MOBILE_NAV_FALLBACK_HEIGHT[mobileNavVariant] : 0
    );

    const nextState = resolveMobileSecondaryNavState(
      {
        anchor: navVisibilityAnchorRef.current,
        isVisible: isMobileNavVisible,
        transitionState: navTransitionStateRef.current,
      },
      {
        hasVariant: Boolean(mobileNavVariant),
        hideDistance: 64,
        isAtTop: isEffectivelyAtTop,
        navHeight: mobileNavHeight,
        scrollDirection: effectiveScrollDirection,
        scrollY: effectiveScrollY,
        showDistance: 24,
      }
    );

    if (
      nextState.anchor === navVisibilityAnchorRef.current &&
      nextState.isVisible === isMobileNavVisible &&
      nextState.transitionState === navTransitionStateRef.current
    ) {
      return;
    }

    navVisibilityAnchorRef.current = nextState.anchor;
    navTransitionStateRef.current = nextState.transitionState;
    setIsMobileNavVisible(nextState.isVisible);
  }, [
    effectiveScrollDirection,
    effectiveScrollY,
    isEffectivelyAtTop,
    isMobileNavVisible,
    mobileNavVariant,
  ]);

  const showMobileSearchBar = mobileNavVariant ? isMobileNavVisible : false;

  // Load navigation categories from API
  useEffect(() => {
    const loadNavigationCategories = async () => {
      try {
        await productStore.loadCategoryNavigation();
        // Filter categories that should appear in the featured menu
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load navigation categories:', error);
      }
    };

    loadNavigationCategories();
  }, [productStore]);

  useEffect(() => {
    if (pathname === '/') {
      return;
    }

    if (
      productStore.hasLoadedMarketplaceHomeContent ||
      productStore.isLoadingMarketplaceHomeContent ||
      productStore.marketplaceHomeContentRetryExhausted
    ) {
      return;
    }

    void productStore.loadMarketplaceHomeContent();
  }, [
    pathname,
    productStore,
    productStore.hasLoadedMarketplaceHomeContent,
    productStore.isLoadingMarketplaceHomeContent,
    productStore.marketplaceHomeContentRetryExhausted,
  ]);

  useEffect(() => {
    if (!authStore.isHydrated) {
      return;
    }

    if (!authStore.isAuthenticated) {
      productStore.clearProgramRewardOffers();
      return;
    }

    if (
      productStore.isLoadingProgramRewardOffers ||
      productStore.programRewardOffersRetryExhausted ||
      productStore.hasLoadedProgramRewardOffers
    ) {
      return;
    }

    void productStore.loadProgramRewardOffers();
  }, [
    authStore.isHydrated,
    authStore.isAuthenticated,
    productStore,
    productStore.isLoadingProgramRewardOffers,
    productStore.programRewardOffersRetryExhausted,
    productStore.hasLoadedProgramRewardOffers,
  ]);

  // Load rep score when user is authenticated
  useEffect(() => {
    if (!authStore.isHydrated) return;
    if (authStore.isAuthenticated && authStore.userId) {
      repScoreStore.loadUserRepScore(authStore.userId).catch(() => undefined);
    }
  }, [authStore.isHydrated, authStore.isAuthenticated, authStore.userId, repScoreStore]);

  useEffect(() => {
    return () => {
      clearWalletRetryTimeout();
    };
  }, [clearWalletRetryTimeout]);

  // Keep wallet state scoped to the active authenticated user. The wallet entry
  // stays visible for signed-in customers, while balance preloading quietly
  // retries so funded users see their latest balance when they open it.
  useEffect(() => {
    if (!authStore.isHydrated) return;

    if (!authStore.isAuthenticated || !authStore.userId) {
      lastWalletUserIdRef.current = null;
      clearWalletRetryTimeout();
      shoppingWalletStore.clear();
      return;
    }

    if (lastWalletUserIdRef.current !== authStore.userId) {
      lastWalletUserIdRef.current = authStore.userId;
      clearWalletRetryTimeout();
      shoppingWalletStore.clear();
    }

    void shoppingWalletStore.loadWallet();
  }, [
    authStore.isHydrated,
    authStore.isAuthenticated,
    authStore.userId,
    clearWalletRetryTimeout,
    shoppingWalletStore,
    walletRetryTick,
  ]);

  useEffect(() => {
    if (!authStore.isHydrated || !authStore.isAuthenticated || !authStore.userId) {
      clearWalletRetryTimeout();
      return;
    }

    if (shoppingWalletStore.hasLoaded || shoppingWalletStore.isLoading) {
      clearWalletRetryTimeout();
      return;
    }

    if (walletRetryTimeoutRef.current !== null) {
      return;
    }

    walletRetryTimeoutRef.current = window.setTimeout(() => {
      walletRetryTimeoutRef.current = null;
      setWalletRetryTick((current) => current + 1);
    }, WALLET_PRELOAD_RETRY_MS);
  }, [
    authStore.isHydrated,
    authStore.isAuthenticated,
    authStore.userId,
    clearWalletRetryTimeout,
    shoppingWalletStore.hasLoaded,
    shoppingWalletStore.isLoading,
  ]);

  const handleOpenRepScoreDrawer = () => {
    modalStore.openRepScoreDrawer();
  };

  const shouldShowShoppingWalletEntry =
    authStore.isAuthenticated;

  const handleRequestCallback = () => {
    setCallbackModalOpen(true);
  };

  // Generate initials from user name (like Google/Gmail)
  const getUserInitials = (): string => {
    const firstName = authStore.currentUser?.first_name || '';
    const lastName = authStore.currentUser?.last_name || '';

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const signUserOut = async () => {
    await authStore.signOut();
    router.replace('/logout');
  };

  /**
   * MOBILE SECONDARY NAVIGATION
   * ---------------------------
   * Get the navigation variant for the current route using the configuration.
   * This determines what shows in the collapsible secondary nav row.
   */
  const announcementSurface = inferAnnouncementSurface(pathname);
  const orderedHeroBanners = useMemo(
    () =>
      isPartnerLoading
        ? []
        : buildOrderedHeroBanners(productStore.marketplaceHeroBanners, {
            isPartnerDomain,
            partnerKey,
          }),
    [
      productStore.marketplaceHeroBanners,
      isPartnerLoading,
      isPartnerDomain,
      partnerKey,
    ]
  );
  const campaignAnnouncement = useMemo(
    () =>
      isPartnerLoading
        ? null
        : getCampaignAnnouncement(
            productStore.marketplaceAnnouncementStrips,
            { surface: announcementSurface, heroBanners: orderedHeroBanners }
          ),
    [
      productStore.marketplaceAnnouncementStrips,
      orderedHeroBanners,
      announcementSurface,
      isPartnerLoading,
    ]
  );
  const rewardOfferAnnouncement = useMemo(() => {
    if (!authStore.isAuthenticated || isPartnerLoading) {
      return null;
    }
    return getRewardOfferAnnouncement(productStore.programRewardOffers);
  }, [authStore.isAuthenticated, isPartnerLoading, productStore.programRewardOffers]);
  const isSpinAcquiredUser =
    (authStore.currentUser?.acquisition_source || '').trim().toLowerCase() ===
    'spin_play';
  const { announcement: effectiveAnnouncement, walletAnnouncement, dismiss: dismissAnnouncement } =
    useWalletGiftAnnouncement({
      wallet: shoppingWalletStore,
      userId: authStore.userId,
      loadedUserId: lastWalletUserIdRef.current,
      ready: authStore.isHydrated && authStore.isAuthenticated && !isPartnerLoading,
      reward: rewardOfferAnnouncement,
      campaign: campaignAnnouncement,
      spinAcquired: isSpinAcquiredUser,
      pathname,
    });
  const shouldShowAnnouncement = Boolean(effectiveAnnouncement);

  return (
    <>
      {/* Mobile Side Drawer */}
      <MobileSideDrawer
        open={isMobileDrawerOpen}
        onOpenChange={setMobileDrawerOpen}
        onRequestCallback={handleRequestCallback}
        showShoppingWallet={shouldShowShoppingWalletEntry}
        onOpenShoppingWallet={() => modalStore.openShoppingWalletDrawer()}
      />

      {/* Shopping Wallet Drawer */}
      <ShoppingWalletDrawer
        open={modalStore.isShoppingWalletDrawerOpen}
        onOpenChange={(open) => modalStore.setShoppingWalletDrawerOpen(open)}
      />

      {/* Rep Score Drawer */}
      <RepScoreDrawer
        open={modalStore.isRepScoreDrawerOpen}
        onOpenChange={(open) => modalStore.setRepScoreDrawerOpen(open)}
      />

      {/* Pay4Me education sheet on mobile and right drawer on larger screens */}
      <Pay4MeDrawer
        open={modalStore.isPay4MeDrawerOpen}
        source={modalStore.pay4MeEntrySource}
        productId={modalStore.pay4MeProductId}
        returnFocusElement={modalStore.pay4MeReturnFocusElement}
        onOpenChange={(open) => modalStore.setPay4MeDrawerOpen(open)}
        onFocusRestored={() => modalStore.clearPay4MeReturnFocusElement()}
      />

      {/* Help Center Sheet - renders Help Center content in-app instead of external link */}
      <HelpCenterSheet
        open={modalStore.isHelpCenterSheetOpen}
        onOpenChange={(open) => modalStore.setHelpCenterSheetOpen(open)}
        articlePath={modalStore.helpCenterArticlePath}
      />

      {/* Request Callback Modal - rendered outside Sheet to prevent unmounting issues */}
      <RequestCallbackModal
        open={isCallbackModalOpen}
        onOpenChange={setCallbackModalOpen}
      />

      {/*
        =======================================================================
        STICKY HEADER CONTAINER
        =======================================================================
        - sticky top-0: Sticks to top of viewport when scrolling
        - z-50: Ensures header stays above other content
        - bg-white: Solid background to cover content scrolling underneath
        - shadow-sm: Subtle shadow when content scrolls under (optional)
        =======================================================================
      */}
      <div ref={primaryStickyShellRef} className="sticky top-0 z-50 bg-white">
        <header
          className={`container pt-5 max-sm:px-6 ${
            mobileNavVariant ? 'pb-0 md:pb-5' : 'pb-5'
          }`}
        >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="block sm:hidden"
              aria-label="Open menu"
            >
              <Image
                src="/assets/svgs/Menu.svg"
                alt="Menu"
                width={24}
                height={24}
              />
            </button>
            <Image
              onClick={() => router.replace('/')}
              src="/assets/svgs/motito.svg"
              alt="Logo"
              width={89}
              height={24}
              className="min-h-6 cursor-pointer"
            />
          </div>
          {!pathname.includes('/checkout') && !pathname.includes('/profile') && (
            <SearchBar className="hidden flex-auto gap-3 md:flex" />
          )}

          {!authStore.isHydrated ? (
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="h-10 w-20 rounded-full" />
              <Skeleton className="h-10 w-20 rounded-full" />
            </div>
          ) : !authStore.isAuthenticated ? (
            <div className="flex gap-2">
              <Button
                size="lg"
                className="hidden rounded-full bg-motitoBlue md:block"
                onClick={() => router.push('/sign-up')}
              >
                Sign Up
              </Button>
              <Button
                variant="outline"
                size="lg"
                className=" rounded-full"
                onClick={() => router.push('/sign-in')}
              >
                Log In
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              {/* Reputation Badge */}
              <ReputationBadge
                onClick={handleOpenRepScoreDrawer}
                className="hidden sm:flex"
              />

              <UserNotificationsPopover enabled={authStore.isAuthenticated} />

              <p className="hidden sm:block">Hi, {authStore.userDisplayName}</p>
              <Link href="/profile">
                <Avatar className="size-9">
                  <AvatarImage src={authStore.photoUrl} />
                  <AvatarFallback className="bg-motitoBlue text-white text-sm font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Link>

              <Popover
                onOpenChange={(open) => {
                  if (!open) return;
                  router.prefetch('/profile');
                  router.prefetch('/order');
                }}
              >
                <PopoverTrigger>
                  <Image
                    src="/assets/svgs/Icons/CaretDown.svg"
                    width={16}
                    height={16}
                    alt="caret"
                  />
                </PopoverTrigger>
                <PopoverContent className="flex w-56 flex-col gap-1 p-2">
                  {/* User info header */}
                  <div className="px-2 py-2">
                    <p className="font-medium text-gray-900">
                      {authStore.userFullDisplayName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {authStore.userEmail || authStore.userPhoneNumber || ''}
                    </p>
                  </div>

                  <div className="my-1 h-px bg-gray-200" />

                  <PopoverClose asChild>
                    <Link href="/profile" className="w-full">
                      <Button className="w-full justify-start gap-2" variant="ghost">
                        <UserCircle className="size-4" />
                        Profile
                      </Button>
                    </Link>
                  </PopoverClose>

                  <PopoverClose asChild>
                    <Link href="/order" className="w-full">
                      <Button className="w-full justify-start gap-2" variant="ghost">
                        <Package className="size-4" />
                        Orders
                      </Button>
                    </Link>
                  </PopoverClose>

                  {shouldShowShoppingWalletEntry ? (
                    <PopoverClose asChild>
                      <Button
                        className="w-full justify-start gap-2"
                        variant="ghost"
                        onClick={() => modalStore.openShoppingWalletDrawer()}
                        data-testid="shopping-wallet-header-menu-item"
                      >
                        <Wallet className="size-4" />
                        Shopping Wallet
                      </Button>
                    </PopoverClose>
                  ) : null}

                  <PopoverClose asChild>
                    <Button
                      className="w-full justify-start gap-2"
                      variant="ghost"
                      onClick={handleOpenRepScoreDrawer}
                    >
                      <Award className="size-4" />
                      Rep Score
                    </Button>
                  </PopoverClose>

                  <div className="my-1 h-px bg-gray-200" />

                  <PopoverClose asChild>
                    <Button
                      onClick={signUserOut}
                      variant="ghost"
                      className="w-full justify-start gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <LogOut className="size-4" />
                      Logout
                    </Button>
                  </PopoverClose>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {shouldShowAnnouncement ? (
          <CampaignAnnouncementStrip
            key={effectiveAnnouncement?.id}
            announcement={effectiveAnnouncement}
            wrapMessage={shouldUseWalletAnnouncementAction(effectiveAnnouncement, walletAnnouncement)}
            onDismiss={dismissAnnouncement}
            onCtaClick={shouldUseWalletAnnouncementAction(effectiveAnnouncement, walletAnnouncement)
              ? () => modalStore.openShoppingWalletDrawer() : undefined}
          />
        ) : null}

      </header>
      <nav className=" hidden border-y text-sm font-medium md:flex ">
        {/* eslint-disable-next-line no-nested-ternary */}
        {pathname.includes('/checkout') ? null : pathname.includes(
            'partner'
          ) ? (
          <PartnerHeader />
        ) : (
          <ul className="container flex items-center justify-center  gap-5 py-3 text-sm">
            {productStore.isLoading && (
              <li className="text-[#3C3C3C]">Loading categories...</li>
            )}
            {!productStore.isLoading &&
              menuItemList.map((item) => (
                <Link
                  href={`/category/${item.slug}`}
                  className="cursor-pointer text-[#3C3C3C] underline-offset-2 transition-all hover:font-bold hover:underline"
                  key={item.id}
                >
                  {item.name}
                </Link>
              ))}
          </ul>
        )}
      </nav>
      </div>
      {/*
        =====================================================================
        MOBILE SECONDARY NAVIGATION (HeaderSecondaryNav)
        =====================================================================
        Rendered as its own sticky layer so collapsing this row does not change
        the sticky geometry of the primary header.
        =====================================================================
      */}
      {mobileNavVariant ? (
        <div
          ref={mobileSecondaryLayerRef}
          className="sticky z-40 bg-white md:hidden"
          style={{ top: mobileSecondaryTopOffset }}
        >
          <div className="container max-sm:px-6">
            <HeaderSecondaryNav
              variant={mobileNavVariant}
              isVisible={showMobileSearchBar}
            />
          </div>
        </div>
      ) : null}
    </>
  );
});

Header.displayName = 'Header';

export default Header;
